class DeferredMedia extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) {
      poster.addEventListener('click', this.loadContent.bind(this));
    }
    if (this.dataset.autoplay !== 'false') {
      // Preload video in background, swap when ready
      this.preloadAndAutoplay();
    }
  }
  preloadAndAutoplay() {
    // Do not start another preload if already loaded
    if (this.getAttribute('loaded')) {
      return;
    }

    const template = this.querySelector('template');
    if (!template) return;

    const wrapper = document.createElement('div');
    wrapper.appendChild(template.content.firstElementChild.cloneNode(true));
    const video = wrapper.querySelector('video');
    if (!video) return;

    // Ensure autoplay requirements are met
    video.muted = true;
    video.preload = 'auto';
    video.autoplay = false;
    // Hide the video while it preloads
    video.style.position = 'absolute';
    video.style.opacity = '0';
    video.style.pointerEvents = 'none';
    // Append to DOM to start loading
    this.appendChild(video);

    video.load();

    // Once enough data is buffered, swap out the poster
    video.addEventListener('canplaythrough', () => {
      // Remove hiding styles
      video.style.position = '';
      video.style.opacity = '';
      video.style.pointerEvents = '';
       // Start playback and handle potential autoplay rejection
       const playResult = video.play();
       if (playResult && typeof playResult.then === 'function') {
         playResult
           .then(() => {
             // Set loaded attr (CSS hides the poster) only after playback starts
             this.setAttribute('loaded', true);
           })
           .catch((error) => {
             // If playback is blocked, keep the poster visible and log a warning
             video.style.position = 'absolute';
             video.style.opacity = '0';
             video.style.pointerEvents = 'none';
             // eslint-disable-next-line no-console
             console.warn('DeferredMedia autoplay failed:', error);
           });
       } else {
         // Older browsers: play() did not return a promise; assume success
         this.setAttribute('loaded', true);
       }
    }, { once: true });
  }

  loadContent(focus = true) {
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        const playResult = deferredElement.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult.catch(() => {
            // Ignore autoplay rejection here; user can start playback manually
          });
        }
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

class MuteButton extends HTMLElement {
  connectedCallback() {
    this._onClick = this.toggle.bind(this);
    this.addEventListener('click', this._onClick);

    this._syncState = this.syncState.bind(this);

    // Observe deferred-media for when the video element gets added
    const section = this.closest('.video-section__body');
    const deferredMedia = section && section.querySelector('deferred-media');
    if (deferredMedia) {
      this._observer = new MutationObserver(() => {
        const video = deferredMedia.querySelector('video');
        if (video) {
          this.syncState();
          video.addEventListener('volumechange', this._syncState);
          this._observer.disconnect();
          this.classList.remove('not-loaded');
        }
      });
      this._observer.observe(deferredMedia, { childList: true, subtree: true });
    }

    this.syncState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this._observer) this._observer.disconnect();

    const video = this.findVideo();
    if (video) video.removeEventListener('volumechange', this._syncState);
  }

  findVideo() {
    const target = this.getAttribute('data-mute-target');
    const container = this.closest(`.${target}`);
    if (!container) return null;
    return container.querySelector('deferred-media video');
  }

  syncState() {
    const video = this.findVideo();
    if (video && video.muted) {
      this.classList.add('is-muted');
      this.setAttribute('aria-label', 'Unmute video');
    } else if (video) {
      this.classList.remove('is-muted');
      this.setAttribute('aria-label', 'Mute video');
    }
  }

  toggle() {
    const video = this.findVideo();
    if (!video) return;
    video.muted = !video.muted;
    this.syncState();
  }
}

if (!customElements.get('mute-button')) {
  customElements.define('mute-button', MuteButton);
}
